import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shuffle, Sparkles, BookOpen, Loader2, ArrowUpRight, History, GraduationCap, Car, Lightbulb, CalendarClock, ChevronDown } from "lucide-react";
import { WelcomeBanner } from "@/components/WelcomeBanner";
import { toast } from "sonner";
import { http, runJob, errMsg, DOMAIN_SHORT } from "@/lib/api";
import { DisclaimerBanner } from "@/components/Disclaimer";
import { DomainCasesDialog } from "@/components/DomainCasesDialog";
import { WeakSpots } from "@/components/WeakSpots";
import { ExamPlan } from "@/components/ExamPlan";
import { GuestSaveBanner } from "@/components/GuestSaveBanner";
import { useAuth } from "@/context/Auth";

const SOURCES = [
  { key: "bank", label: "Exam bank", icon: BookOpen },
  { key: "ai", label: "AI-written new case", icon: Sparkles },
  { key: "mixed", label: "Either", icon: Shuffle },
];

const SourceToggle = ({ value, onChange }) => (
  <div data-testid="toggle-ai-case-gen" className="inline-flex p-1 rounded-full flex-wrap" style={{ background: "var(--muted-bg)" }}>
    {SOURCES.map(({ key, label, icon: Icon }) => (
      <button
        key={key}
        data-testid={`source-${key}-button`}
        onClick={() => onChange(key)}
        className="px-4 h-9 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors"
        style={value === key ? { background: "var(--surface)", color: "var(--ink)", boxShadow: "0 1px 3px rgba(0,0,0,.12)" } : { color: "var(--ink-3)" }}
      >
        <Icon size={14} /> {label}
      </button>
    ))}
  </div>
);

const DomainCard = ({ d, onClick, disabled }) => (
  <button
    data-testid={`domain-card-${d.number}`}
    onClick={onClick}
    disabled={disabled}
    className="card group w-full h-full text-left p-5 flex flex-col gap-3 transition-[transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-[var(--accent)] disabled:opacity-60"
  >
    <div className="flex items-center justify-between">
      <span className="font-mono text-xs font-bold" style={{ color: "var(--accent)" }}>D{String(d.number).padStart(2, "0")}</span>
      <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--accent)" }} />
    </div>
    <h3 className="font-display font-semibold leading-snug">{d.name}</h3>
    <p className="text-xs mt-auto" style={{ color: "var(--ink-3)" }}>
      {d.bank_count} ready-made cases{d.ai_count ? ` · ${d.ai_count} AI cases` : ""}
    </p>
  </button>
);

const RecentAttempts = ({ items, onOpen }) => {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <section className="mt-14" data-testid="recent-attempts">
      <button data-testid="recent-attempts-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="w-full flex items-center justify-between text-left group">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><History size={18} /> Recent attempts <span className="font-mono text-xs font-normal" style={{ color: "var(--ink-3)" }}>({Math.min(items.length, 8)})</span></h2>
        <span className="text-sm flex items-center gap-1" style={{ color: "var(--ink-3)" }}>{open ? "Hide" : "Show"} <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} /></span>
      </button>
      {open && (
      <div data-testid="recent-attempts-list" className="mt-4 card divide-y fade-up" style={{ borderColor: "var(--line)" }}>
        {items.slice(0, 8).map((a) => (
          <button key={a.id} data-testid={`attempt-row-${a.id}`} onClick={() => onOpen(a.id)} className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-[var(--muted-bg)] transition-colors" style={{ borderColor: "var(--line)" }}>
            <span className="text-sm">
              <span className="font-mono text-xs mr-2" style={{ color: "var(--ink-3)" }}>{DOMAIN_SHORT[a.domain - 1]}</span>
              {a.case_title}
            </span>
            <span className="font-mono text-sm font-bold">{a.overall_score}%</span>
          </button>
        ))}
      </div>
      )}
    </section>
  );
};

export default function Home() {
  const nav = useNavigate();
  const [domains, setDomains] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [source, setSource] = useState("bank");
  const [loading, setLoading] = useState(null);
  const [stats, setStats] = useState([]);
  const [openDomain, setOpenDomain] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user === null) return;
    http.get("/domains").then((r) => setDomains(r.data));
    http.get("/attempts").then((r) => setAttempts(r.data));
    http.get("/stats/domains").then((r) => setStats(r.data));
  }, [user]);

  const lastScores = {};
  [...attempts].reverse().forEach((a) => { lastScores[a.case_id] = a.overall_score; });

  const start = async (domain, forced) => {
    const src = forced || (source === "mixed" ? (Math.random() < 0.5 ? "bank" : "ai") : source);
    setLoading(src === "ai" ? "Writing a new viva station… (up to a minute)" : "Loading case…");
    try {
      const data = src === "ai"
        ? await runJob("/cases/generate", { domain })
        : (await http.post("/cases/start", { domain, source: src })).data;
      nav(`/viva/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setLoading(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <DisclaimerBanner />
      <WelcomeBanner />
      <section className="mt-12 grid md:grid-cols-12 gap-8 items-end fade-up">
        <div className="md:col-span-8">
          <p className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color: "var(--accent)" }}>ANZCA · ACRRM · RACGP — RGA AST</p>
          <p data-testid="home-affiliation-note" className="mt-1 text-xs" style={{ color: "var(--ink-3)" }}>(RGA Trainer is not affiliated with, nor endorsed by, any college or other organisation)</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            SSSA mock viva,<br />15 minutes at a time.
          </h1>
          <p className="mt-5 text-base max-w-xl" style={{ color: "var(--ink-2)" }}>
            3 minutes to read the stem, 12 minutes of examiner questions. Speak your answers — get examiner-style feedback against the model answer at the end.
          </p>
        </div>
        <div className="md:col-span-4 flex flex-col md:items-end gap-3">
          <button data-testid="start-random-case-button" onClick={() => start(null)} disabled={!!loading} className="btn-primary h-14 px-7 rounded-full font-semibold flex items-center gap-2 text-base">
            <Shuffle size={18} /> Random case
          </button>
          <button data-testid="mock-exam-button" onClick={() => nav("/mock")} className="btn-ghost h-12 px-6 rounded-full font-semibold flex items-center gap-2 text-sm">
            <CalendarClock size={17} style={{ color: "var(--accent)" }} /> Mock Exam Day (8 stations)
          </button>
          <button data-testid="college-questions-button" onClick={() => nav("/college")} className="btn-ghost h-12 px-6 rounded-full font-semibold flex items-center gap-2 text-sm">
            <GraduationCap size={17} style={{ color: "var(--accent)" }} /> ANZCA college questions
          </button>
          <button data-testid="driving-mode-button" onClick={() => nav("/driving")} className="btn-ghost h-12 px-6 rounded-full font-semibold flex items-center gap-2 text-sm">
            <Car size={17} style={{ color: "var(--accent)" }} /> Driving mode (hands-free)
          </button>
          <button data-testid="exam-prep-button" onClick={() => nav("/exam-prep")} className="btn-ghost h-12 px-6 rounded-full font-semibold flex items-center gap-2 text-sm">
            <Lightbulb size={17} style={{ color: "var(--accent)" }} /> Exam Prep Advice
          </button>
        </div>
      </section>

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <SourceToggle value={source} onChange={setSource} />
        {loading && (
          <span data-testid="case-loading-indicator" className="text-sm flex items-center gap-2" style={{ color: "var(--ink-2)" }}>
            <Loader2 size={16} className="animate-spin" /> {loading}
          </span>
        )}
      </div>

      <section className="mt-8">
        <h2 className="font-display text-lg font-bold">Or choose a domain</h2>
        <p className="text-sm mt-1" style={{ color: "var(--ink-3)" }}>Open a domain to browse its cases, pick one, or generate a new AI case.</p>
        <div data-testid="domain-selector-grid" className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {domains.map((d, i) => (
            <div key={d.number} className="fade-up h-full" style={{ animationDelay: `${i * 40}ms` }}>
              <DomainCard d={d} onClick={() => setOpenDomain(d)} disabled={!!loading} />
            </div>
          ))}
        </div>
      </section>

      <ExamPlan stats={stats} onOpen={(n) => setOpenDomain(domains.find((d) => d.number === n))} />
      <WeakSpots stats={stats} onOpen={(n) => setOpenDomain(domains.find((d) => d.number === n))} />
      {attempts.length > 0 && <div className="mt-10"><GuestSaveBanner testid="home-guest-banner" /></div>}
      <RecentAttempts items={attempts} onOpen={(id) => nav(`/results/${id}`)} />
      <DomainCasesDialog
        domain={openDomain}
        onClose={() => !loading && setOpenDomain(null)}
        onPick={(id) => nav(`/viva/${id}`)}
        onRandom={() => start(openDomain.number, "bank")}
        onGenerate={() => start(openDomain.number, "ai")}
        lastScores={lastScores}
        loading={loading}
      />
    </div>
  );
}
